import React, { useState, useEffect, useRef } from 'react';
import Icon from '../AppIcon';

const DynamicDropdown = ({ 
  label, 
  value, 
  onChange, 
  options, 
  onAddOption, 
  placeholder = "Select or add new...",
  className = "",
  error = null,
  required = false,
  disabled = false,
  addNewText = "Add new option"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddNew, setShowAddNew] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Filter options based on search term
  const filteredOptions = options.filter(option =>
    option.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Check if search term matches any existing option
  const exactMatch = options.find(option => 
    option.name.toLowerCase() === searchTerm.toLowerCase()
  );

  // Show "Add new" option if search term doesn't match any existing option
  const shouldShowAddNew = searchTerm.length > 0 && !exactMatch;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
        setShowAddNew(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle option selection
  const handleOptionSelect = (option) => {
    onChange(option);
    setIsOpen(false);
    setSearchTerm('');
    setShowAddNew(false);
  };

  // Handle adding new option
  const handleAddNew = async () => {
    if (!searchTerm.trim() || isAdding) return;

    console.log('Adding new option:', searchTerm.trim());
    setIsAdding(true);
    try {
      const newOption = await onAddOption(searchTerm.trim());
      console.log('New option created:', newOption);
      if (newOption) {
        onChange(newOption);
        setIsOpen(false);
        setSearchTerm('');
        setShowAddNew(false);
      }
    } catch (error) {
      console.error('Error adding new option:', error);
      alert('Error adding new option: ' + error.message);
    } finally {
      setIsAdding(false);
    }
  };

  // Handle input change
  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    if (!isOpen) setIsOpen(true);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && shouldShowAddNew && !isAdding) {
      e.preventDefault();
      handleAddNew();
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
      setShowAddNew(false);
    }
  };

  // Get display value
  const displayValue = value ? value.name : '';

  return (
    <div className="relative" ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-medium text-text-primary mb-2">
          {label} {required && <span className="text-error">*</span>}
        </label>
      )}
      
      <div className="relative">
          <input
          ref={inputRef}
          type="text"
          value={isOpen ? searchTerm : displayValue}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={`
            w-full px-3 py-2 border rounded-md text-sm bg-surface
            focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
            disabled:bg-muted disabled:text-text-muted disabled:cursor-not-allowed
            ${error ? 'border-error' : 'border-border'}
            ${className}
          `}
        />
        
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <Icon 
            name={isOpen ? "ChevronUp" : "ChevronDown"} 
            size={16} 
            className="text-text-secondary" 
          />
        </div>
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-surface border border-border rounded-md shadow-lg max-h-60 overflow-y-auto">
          {/* Filtered Options */}
          {filteredOptions.length > 0 && (
            <div className="py-1">
              {filteredOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => handleOptionSelect(option)}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-surface-hover focus:bg-surface-hover focus:outline-none"
                >
                  <span className="text-text-primary">{option.name}</span>
                </button>
              ))}
            </div>
          )}

          {/* Add New Option */}
          {shouldShowAddNew && (
            <>
              {filteredOptions.length > 0 && <div className="border-t border-border" />}
              <div className="py-1">
                <button
                  type="button"
                  onClick={handleAddNew}
                  disabled={isAdding}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-primary-50 focus:bg-primary-50 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="flex items-center">
                    <Icon 
                      name={isAdding ? "Loader" : "Plus"} 
                      size={16} 
                      className={`mr-2 ${isAdding ? 'animate-spin' : ''} text-primary`} 
                    />
                    <span className="text-primary font-medium">
                      {isAdding ? 'Adding...' : `${addNewText}: "${searchTerm}"`}
                    </span>
                  </div>
                </button>
              </div>
            </>
          )}

          {/* No Options Message */}
          {filteredOptions.length === 0 && !shouldShowAddNew && (
            <div className="px-3 py-2 text-sm text-text-secondary">
              No options found
            </div>
          )}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <p className="mt-1 text-sm text-error">{error}</p>
      )}
    </div>
  );
};

export default DynamicDropdown; 